import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Gift, Copy, Check, Users, MousePointer, UserPlus, DollarSign,
  ArrowRight, Mail, MessageSquare, Link2, Share2, TrendingUp,
  BarChart3, Eye,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, iconColor, label, value, sub }) {
  return (
    <div className="p-4 rounded-xl bg-card border border-border" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${iconColor}`}>
          <Icon size={16} />
        </div>
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function FunnelBar({ label, value, max, color }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="text-foreground font-medium">{value}</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function ReferralDashboardPage() {
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/growth/referral/stats`, { headers }),
      axios.get(`${API}/growth/referral/share-data`, { headers }),
      axios.get(`${API}/growth/analytics`, { headers }),
    ]).then(([s, sh, a]) => {
      setStats(s.data);
      setShareData(sh.data);
      setAnalytics(a.data);
    }).catch(() => toast.error('Failed to load referral data'))
      .finally(() => setLoading(false));
  }, [token]);

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopied(''), 2000);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  const funnelMax = Math.max(stats?.total_clicks || 1, 1);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-5" data-testid="referral-dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
            <Gift size={22} className="text-emerald-400" /> Growth & Referrals
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Share your AI, earn rewards, and grow your network</p>
        </div>
      </div>

      {/* Share Section */}
      <Card className="border-emerald-500/15 bg-gradient-to-br from-primary/5/20 to-card" data-testid="share-section">
        <CardHeader className="pb-2 pt-4 px-5">
          <CardTitle className="text-foreground text-sm flex items-center gap-1.5">
            <Share2 size={15} className="text-emerald-400" /> Share Your AI Assistant
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          {/* Referral Link */}
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Your Referral Link</label>
            <div className="flex gap-2">
              <div className="flex-1 h-9 rounded-lg bg-muted border border-border text-sm text-foreground px-3 flex items-center overflow-hidden" data-testid="referral-link-display">
                <span className="truncate">{shareData?.referral_url || '...'}</span>
              </div>
              <Button size="sm" onClick={() => copyText(shareData?.referral_url, 'Referral link')}
                className="h-9 px-3 bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 border border-emerald-500/20"
                data-testid="copy-referral-link">
                {copied === 'Referral link' ? <Check size={14} /> : <Copy size={14} />}
              </Button>
            </div>
          </div>

          {/* Demo Link */}
          {shareData?.demo_url && (
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Demo Link</label>
              <div className="flex gap-2">
                <div className="flex-1 h-9 rounded-lg bg-muted border border-border text-sm text-foreground px-3 flex items-center overflow-hidden" data-testid="demo-link-display">
                  <span className="truncate">{shareData?.demo_url}</span>
                </div>
                <Button size="sm" onClick={() => copyText(shareData?.demo_url, 'Demo link')}
                  className="h-9 px-3 bg-sky-500/15 text-sky-400 hover:bg-sky-500/25 border border-sky-500/20"
                  data-testid="copy-demo-link">
                  {copied === 'Demo link' ? <Check size={14} /> : <Copy size={14} />}
                </Button>
              </div>
            </div>
          )}

          {/* Quick Share */}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => copyText(shareData?.sms_message, 'SMS message')}
              className="h-8 text-xs px-3 gap-1.5 bg-muted text-foreground hover:text-foreground hover:bg-accent border border-border"
              data-testid="share-sms">
              <MessageSquare size={12} /> {copied === 'SMS message' ? 'Copied!' : 'Copy SMS'}
            </Button>
            <Button size="sm" onClick={() => {
              const subject = encodeURIComponent(shareData?.email_subject || '');
              const body = encodeURIComponent(shareData?.email_body || '');
              window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
            }}
              className="h-8 text-xs px-3 gap-1.5 bg-muted text-foreground hover:text-foreground hover:bg-accent border border-border"
              data-testid="share-email">
              <Mail size={12} /> Email
            </Button>
            <Button size="sm" onClick={() => copyText(shareData?.share_message, 'Share message')}
              className="h-8 text-xs px-3 gap-1.5 bg-muted text-foreground hover:text-foreground hover:bg-accent border border-border"
              data-testid="share-social">
              <Link2 size={12} /> {copied === 'Share message' ? 'Copied!' : 'Copy for Social'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={MousePointer} iconColor="bg-sky-500/10 text-sky-400" label="Link Clicks" value={stats?.total_clicks || 0} />
        <StatCard icon={UserPlus} iconColor="bg-violet-500/10 text-violet-400" label="Signups" value={stats?.total_signups || 0} />
        <StatCard icon={Users} iconColor="bg-emerald-500/10 text-emerald-400" label="Conversions" value={stats?.total_conversions || 0} />
        <StatCard icon={DollarSign} iconColor="bg-amber-500/10 text-amber-400" label="Earned"
          value={`$${(stats?.total_earned || 0).toFixed(0)}`}
          sub={stats?.pending_rewards > 0 ? `$${stats.pending_rewards.toFixed(0)} pending` : null} />
      </div>

      {/* Funnel + Analytics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Referral Funnel */}
        <Card className="border-border bg-card" data-testid="referral-funnel">
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-foreground text-sm flex items-center gap-1.5">
              <TrendingUp size={14} className="text-emerald-400" /> Referral Funnel
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5 space-y-3">
            <FunnelBar label="Clicks" value={stats?.total_clicks || 0} max={funnelMax} color="bg-sky-500" />
            <FunnelBar label="Signups" value={stats?.total_signups || 0} max={funnelMax} color="bg-violet-500" />
            <FunnelBar label="Conversions" value={stats?.total_conversions || 0} max={funnelMax} color="bg-emerald-500" />
            <div className="pt-2 border-t border-border flex justify-between text-[10px]">
              <span className="text-muted-foreground">Click-to-signup rate</span>
              <span className="text-foreground font-medium">{analytics?.referral?.click_to_signup_rate || 0}%</span>
            </div>
          </CardContent>
        </Card>

        {/* Demo Analytics */}
        <Card className="border-border bg-card" data-testid="demo-analytics">
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-foreground text-sm flex items-center gap-1.5">
              <BarChart3 size={14} className="text-sky-400" /> Demo Analytics
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5 space-y-3">
            <div className="flex justify-between items-center py-2">
              <span className="text-xs text-muted-foreground">Total demo sessions</span>
              <span className="text-lg font-bold text-foreground">{analytics?.demo?.total_sessions || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-t border-border">
              <span className="text-xs text-muted-foreground">Sessions (last 7 days)</span>
              <span className="text-lg font-bold text-foreground">{analytics?.demo?.sessions_7d || 0}</span>
            </div>
            <div className="pt-2 border-t border-border">
              <p className="text-[10px] text-muted-foreground">
                Reward: <span className="text-emerald-400 font-medium">${stats?.reward_per_referral || 25}</span> credit per converted referral
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Signups */}
      {stats?.recent_signups?.length > 0 && (
        <Card className="border-border bg-card" data-testid="recent-signups">
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-foreground text-sm">Recent Referral Signups</CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            <div className="space-y-2">
              {stats.recent_signups.map((s, i) => (
                <div key={r.email || r.id || i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div>
                    <p className="text-sm text-foreground">{s.referred_name || 'Unknown'}</p>
                    <p className="text-[10px] text-muted-foreground">{s.referred_email}</p>
                  </div>
                  <Badge className={`text-[10px] ${
                    s.status === 'converted'
                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                      : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                  }`}>
                    {s.status === 'converted' ? 'Converted' : 'Signed Up'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
